/**
 * Week-column variants of the funnel rows.
 *
 * Composed from the shared cell groups in components/shared/SalesFunnelRows so
 * the Range Total / Conversion / Active Leads / Next 3-5 cells are defined
 * exactly once and cannot drift from the Forecast card.
 *
 * The first cell is sticky. Sticky cells are transparent by default, so each
 * carries an OPAQUE background (never a /60 alpha, which would let scrolled
 * content bleed through) and an inset box-shadow for the seam rather than a
 * border-r — Tailwind's preflight sets border-collapse: collapse, under which
 * borders on sticky cells mis-paint in Safari.
 */

import React from 'react';
import {
  FunnelMetricKey,
  FunnelPeriod,
  SalesFunnelRowP,
  SalesFunnelTotalsP,
} from '../../../../types/salesTeamFunnel';
import {
  ApptDetailsRow,
  FunnelActiveLeadsCell,
  FunnelApptNextCells,
  FunnelConversionCells,
  FunnelCountCells,
  hasApptDetails,
} from '../../../shared/SalesFunnelRows';
import { metricCell, weekColCount } from './funnelWeekHelpers';

const STICKY = 'sticky left-0 z-10 shadow-[inset_-1px_0_0_0_rgb(229,231,235)]';

/** Columns the printed page drops once the table gets too wide (see index.css). */
const PRINT_OPTIONAL = 'funnel-print-optional';

const weekCellClass = (isFirst: boolean) =>
  'px-2 py-2 text-right tabular-nums whitespace-nowrap'
  + (isFirst ? ' border-l border-gray-200' : '');

export const WeekMemberRow: React.FC<{
  row: SalesFunnelRowP;
  rowId: string;
  isOpen: boolean;
  onToggle: (rowId: string) => void;
  periods: FunnelPeriod[];
  metric: FunnelMetricKey;
  nextDates: string[];
  reportDate: string;
  reportDateIsToday: boolean;
}> = ({ row, rowId, isOpen, onToggle, periods, metric, nextDates, reportDate, reportDateIsToday }) => {
  const hasDetails = hasApptDetails(row);
  return (
    <>
      <tr
        className={`border-t border-gray-100 ${hasDetails ? 'cursor-pointer hover:bg-gray-50 select-none' : ''}`}
        onClick={hasDetails ? () => onToggle(rowId) : undefined}
      >
        <td className={`${STICKY} bg-white px-3 py-2 text-gray-900 pl-6 min-w-[200px] max-w-[260px]`}>
          <span className="flex items-center">
            {hasDetails && (
              <span className="inline-block w-3 shrink-0 text-gray-400">{isOpen ? '▾' : '▸'}</span>
            )}
            <span className="truncate" title={row.name}>{row.name}</span>
          </span>
        </td>

        {/* Index against the TOP-LEVEL periods, never row.periods — a short
            row must show em dashes, not shift every column left. */}
        {periods.map((period, i) => (
          <td key={period.key} className={`${weekCellClass(i === 0)} text-gray-900`}>
            {metricCell(metric, row.periods?.[i])}
          </td>
        ))}

        <FunnelCountCells
          leads={row.leads} visits={row.visits} apptsPast={row.apptsPast} sales={row.sales}
          variant="member"
        />
        <FunnelConversionCells
          visitsPerLeadPct={row.visitsPerLeadPct}
          salesPerVisitPct={row.salesPerVisitPct}
          salesPerLeadPct={row.salesPerLeadPct}
          variant="member"
          optionalClass={PRINT_OPTIONAL}
        />
        <FunnelActiveLeadsCell value={row.activeLeads} variant="member" />
        <FunnelApptNextCells
          apptsNext3={row.apptsNext3} apptsNext5={row.apptsNext5}
          variant="member" optionalClass={PRINT_OPTIONAL}
        />
      </tr>
      {hasDetails && isOpen && (
        <ApptDetailsRow
          row={row}
          nextDates={nextDates}
          reportDate={reportDate}
          reportDateIsToday={reportDateIsToday}
          colSpan={weekColCount(periods.length)}
        />
      )}
    </>
  );
};

/** Serves team subtotals, location totals AND the grand total. */
export const WeekTotalsRow: React.FC<{
  label: string;
  totals: SalesFunnelTotalsP;
  periods: FunnelPeriod[];
  metric: FunnelMetricKey;
  emphasis?: boolean;
  activeLeadsUnavailable?: boolean;
}> = ({ label, totals, periods, metric, emphasis = false, activeLeadsUnavailable = false }) => (
  <tr className={emphasis
    ? 'border-t-2 border-gray-300 bg-gray-100 font-semibold text-gray-900'
    : 'border-t border-gray-200 bg-gray-50 font-medium text-gray-700'}>
    <td className={`${STICKY} ${emphasis ? 'bg-gray-100' : 'bg-gray-50'} px-3 py-2 text-left min-w-[200px] max-w-[260px]`}>
      <span className="block truncate" title={label}>{label}</span>
    </td>

    {periods.map((period, i) => (
      <td key={period.key} className={weekCellClass(i === 0)}>
        {metricCell(metric, totals.periods?.[i])}
      </td>
    ))}

    <FunnelCountCells
      leads={totals.leads} visits={totals.visits} apptsPast={totals.apptsPast} sales={totals.sales}
      variant="totals"
    />
    <FunnelConversionCells
      visitsPerLeadPct={totals.visitsPerLeadPct}
      salesPerVisitPct={totals.salesPerVisitPct}
      salesPerLeadPct={totals.salesPerLeadPct}
      variant="totals"
      optionalClass={PRINT_OPTIONAL}
    />
    <FunnelActiveLeadsCell
      value={totals.activeLeads} unavailable={activeLeadsUnavailable} variant="totals"
    />
    <FunnelApptNextCells
      apptsNext3={totals.apptsNext3} apptsNext5={totals.apptsNext5}
      variant="totals" optionalClass={PRINT_OPTIONAL}
    />
  </tr>
);

/** Team band, sized to the week table's width. */
export const WeekTeamHeaderRow: React.FC<{
  managerId: string;
  managerName: string;
  rowCount: number;
  colSpan: number;
}> = ({ managerId, managerName, rowCount, colSpan }) => (
  <tr className="border-t-2 border-gray-200 bg-blue-50">
    <td
      colSpan={colSpan}
      className={`${STICKY} bg-blue-50 px-3 py-1.5 font-semibold text-gray-800`}
    >
      {managerId ? `Team ${managerName}` : 'Unassigned'}
      <span className="ml-2 text-xs font-normal text-gray-500">
        ({rowCount} {rowCount === 1 ? 'salesperson' : 'salespeople'})
      </span>
    </td>
  </tr>
);
